import type { WeeklyAvailability } from '@repo/frontend-shared';

function timeStringToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

function minutesToTimeString(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  return mins > 0 ? `${displayHours}:${mins.toString().padStart(2, '0')} ${period}` : `${displayHours} ${period}`;
}

function getDayRanges(activeDays: number[]): string {
  if (activeDays.length === 0) return 'No days';
  if (activeDays.length === 7) return 'Every day';

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const sorted = [...activeDays].sort((a, b) => a - b);

  // Check for common patterns
  if (sorted.length === 5 && sorted[0] === 1 && sorted[4] === 5) {
    return 'Mon-Fri';
  }
  if (sorted.length === 2 && sorted[0] === 0 && sorted[1] === 6) {
    return 'Sat-Sun';
  }

  // Build ranges
  const ranges: string[] = [];
  let rangeStart = sorted[0];
  let prev = sorted[0];

  for (let i = 1; i <= sorted.length; i++) {
    const current = sorted[i];

    if (current !== prev + 1 || i === sorted.length) {
      // End of range or end of array
      if (rangeStart === prev) {
        ranges.push(dayNames[rangeStart]);
      } else if (prev === rangeStart + 1) {
        ranges.push(`${dayNames[rangeStart]}, ${dayNames[prev]}`);
      } else {
        ranges.push(`${dayNames[rangeStart]}-${dayNames[prev]}`);
      }

      rangeStart = current;
    }

    prev = current;
  }

  return ranges.join(', ');
}

function getTimeRange(settings: WeeklyAvailability['settings']): { earliest: number; latest: number } | null {
  let earliest = Number.POSITIVE_INFINITY;
  let latest = Number.NEGATIVE_INFINITY;

  settings.forEach(setting => {
    if (setting.isAvailable && setting.intervals && setting.intervals.length > 0) {
      setting.intervals.forEach(interval => {
        const startMinutes = timeStringToMinutes(interval.start);
        const endMinutes = timeStringToMinutes(interval.end);
        earliest = Math.min(earliest, startMinutes);
        latest = Math.max(latest, endMinutes);
      });
    }
  });

  if (earliest === Number.POSITIVE_INFINITY || latest === Number.NEGATIVE_INFINITY) {
    return null;
  }

  return { earliest, latest };
}

export function getScheduleSummary(schedule: WeeklyAvailability): string {
  if (!schedule.settings || schedule.settings.length === 0) {
    return 'No availability set';
  }

  // Find active days
  const activeDays = new Set<number>();
  schedule.settings.forEach(setting => {
    if (setting.isAvailable && setting.intervals && setting.intervals.length > 0) {
      activeDays.add(setting.weekDay);
    }
  });

  if (activeDays.size === 0) {
    return 'No availability set';
  }

  const dayRanges = getDayRanges(Array.from(activeDays));
  const timeRange = getTimeRange(schedule.settings);

  if (!timeRange) {
    return dayRanges;
  }

  const startTime = minutesToTimeString(timeRange.earliest);
  const endTime = minutesToTimeString(timeRange.latest);

  return `${dayRanges} ${startTime}-${endTime}`;
}
